/**
 * JSONC type-schema engine.
 * Schema values are type tokens; structure mirrors the case JSON.
 * Tokens: string | number | boolean | date | datetime | locale | timezone |
 *   media_url | https_url | object | enum:a|b | const:x | digits:N
 * key? = optional field. [T] = array of T.
 */

import {
  digitsError,
  formatError,
  parseDigitsWidth,
  type FormatToken,
} from './formats'
import type { ValidationIssue } from './types'

export type TypeSchema = string | TypeSchema[] | { [key: string]: TypeSchema }

const FORMAT_TOKENS = new Set<FormatToken>([
  'date',
  'datetime',
  'media_url',
  'https_url',
  'locale',
  'timezone',
  'string',
  'number',
  'boolean',
])

/** Strip // and /* *\/ comments and trailing commas; then JSON.parse. */
export function parseJsonc(text: string): unknown {
  const stripped = stripJsonc(text)
  return JSON.parse(stripped)
}

function stripJsonc(text: string): string {
  let out = ''
  let i = 0
  let inString = false
  let stringQuote = ''
  let escaped = false

  while (i < text.length) {
    const ch = text[i]!
    const next = text[i + 1]

    if (inString) {
      out += ch
      if (escaped) {
        escaped = false
      } else if (ch === '\\') {
        escaped = true
      } else if (ch === stringQuote) {
        inString = false
      }
      i += 1
      continue
    }

    if (ch === '"' || ch === "'") {
      inString = true
      stringQuote = ch
      out += ch
      i += 1
      continue
    }

    if (ch === '/' && next === '/') {
      i += 2
      while (i < text.length && text[i] !== '\n') i += 1
      continue
    }

    if (ch === '/' && next === '*') {
      i += 2
      while (i < text.length && !(text[i] === '*' && text[i + 1] === '/')) i += 1
      i += 2
      continue
    }

    out += ch
    i += 1
  }

  // trailing commas before } or ]
  return out.replace(/,(\s*[}\]])/g, '$1')
}

export function loadTypeSchema(source: string): TypeSchema {
  const parsed = parseJsonc(source)
  if (!isSchemaNode(parsed)) {
    throw new Error('case type schema root must be an object, array, or token string')
  }
  return parsed
}

function isSchemaNode(value: unknown): value is TypeSchema {
  if (typeof value === 'string') return true
  if (Array.isArray(value)) {
    return value.length === 1 && isSchemaNode(value[0])
  }
  if (value !== null && typeof value === 'object') {
    return Object.values(value).every(isSchemaNode)
  }
  return false
}

function push(errors: ValidationIssue[], path: string, message: string): void {
  errors.push({ path, message })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/** Parse `key` / `key?` from schema.jsonc object fields. */
export function splitSchemaKey(rawKey: string): {
  key: string
  optional: boolean
} {
  if (rawKey.endsWith('?')) {
    return { key: rawKey.slice(0, -1), optional: true }
  }
  return { key: rawKey, optional: false }
}

export type SchemaObjectField = {
  key: string
  optional: boolean
  schema: TypeSchema
}

function isSchemaRecord(
  schema: TypeSchema,
): schema is { [key: string]: TypeSchema } {
  return typeof schema === 'object' && schema !== null && !Array.isArray(schema)
}

/** Object fields of a type-schema node (empty if not an object). */
export function listObjectFields(schema: TypeSchema): SchemaObjectField[] {
  if (!isSchemaRecord(schema)) return []
  return Object.entries(schema).map(([rawKey, child]) => {
    const { key, optional } = splitSchemaKey(rawKey)
    return { key, optional, schema: child }
  })
}

/** Required field names at this object level. */
export function listRequiredKeys(schema: TypeSchema): string[] {
  return listObjectFields(schema)
    .filter((field) => !field.optional)
    .map((field) => field.key)
}

/** Optional field names at this object level. */
export function listOptionalKeys(schema: TypeSchema): string[] {
  return listObjectFields(schema)
    .filter((field) => field.optional)
    .map((field) => field.key)
}

/** All field names (required then optional, schema order). */
export function listAllKeys(schema: TypeSchema): string[] {
  return listObjectFields(schema).map((field) => field.key)
}

/**
 * Required paths under a schema object.
 * Containers (object/array) and nested required leaves are included.
 * Top-level leaves are omitted (use listRequiredKeys for those).
 */
export function collectRequiredPaths(
  schema: TypeSchema,
  prefix = '',
): string[] {
  const paths: string[] = []
  for (const field of listObjectFields(schema)) {
    if (field.optional) continue
    const path = prefix ? `${prefix}.${field.key}` : field.key
    if (typeof field.schema === 'object' && field.schema !== null) {
      paths.push(path)
      if (!Array.isArray(field.schema)) {
        paths.push(...collectRequiredPaths(field.schema, path))
      }
      continue
    }
    if (prefix) paths.push(path)
  }
  return paths
}

/** Field names whose type token is media_url (anywhere in the tree). */
export function collectMediaFieldNames(schema: TypeSchema): Set<string> {
  const names = new Set<string>()
  walkMediaFieldNames(schema, names)
  return names
}

function walkMediaFieldNames(schema: TypeSchema, names: Set<string>): void {
  if (typeof schema === 'string') return
  if (Array.isArray(schema)) {
    walkMediaFieldNames(schema[0]!, names)
    return
  }
  for (const [rawKey, child] of Object.entries(schema)) {
    const { key } = splitSchemaKey(rawKey)
    if (child === 'media_url') {
      names.add(key)
    } else if (Array.isArray(child) && child[0] === 'media_url') {
      names.add(key)
    } else {
      walkMediaFieldNames(child, names)
    }
  }
}

function validateToken(
  errors: ValidationIssue[],
  path: string,
  data: unknown,
  token: string,
): void {
  if (token.startsWith('const:')) {
    const expected = token.slice('const:'.length)
    if (data !== expected) {
      push(errors, path, `expected ${JSON.stringify(expected)}, got ${JSON.stringify(data)}`)
    }
    return
  }

  if (token.startsWith('enum:')) {
    const options = token.slice('enum:'.length).split('|')
    if (typeof data !== 'string' || !options.includes(data)) {
      push(
        errors,
        path,
        `expected one of ${options.map((o) => JSON.stringify(o)).join('|')}, got ${JSON.stringify(data)}`,
      )
    }
    return
  }

  if (token.startsWith('digits:')) {
    const width = parseDigitsWidth(token)
    if (width === null) {
      push(errors, path, `unknown type token "${token}"`)
      return
    }
    const issue = digitsError(width, data)
    if (issue) push(errors, path, issue)
    return
  }

  if (token === 'object') {
    if (!isRecord(data)) {
      push(errors, path, 'expected object')
    }
    return
  }

  if (token === 'string') {
    if (!nonEmptyString(data)) {
      push(errors, path, 'expected a non-empty string')
    }
    return
  }

  if (FORMAT_TOKENS.has(token as FormatToken)) {
    const issue = formatError(token as FormatToken, data)
    if (issue) push(errors, path, issue)
    return
  }

  push(errors, path, `unknown type token "${token}"`)
}

/** Validate data against a type-schema tree. Open objects: extra keys allowed. */
export function validateAgainstTypeSchema(
  data: unknown,
  schema: TypeSchema,
  basePath = '',
): ValidationIssue[] {
  const errors: ValidationIssue[] = []
  walk(errors, basePath, data, schema)
  return errors
}

function walk(
  errors: ValidationIssue[],
  path: string,
  data: unknown,
  schema: TypeSchema,
): void {
  if (typeof schema === 'string') {
    validateToken(errors, path || '(root)', data, schema)
    return
  }

  if (Array.isArray(schema)) {
    if (!Array.isArray(data)) {
      push(errors, path || '(root)', 'expected array')
      return
    }
    const itemSchema = schema[0]!
    data.forEach((item, i) => {
      const itemPath = path ? `${path}[${i}]` : `[${i}]`
      walk(errors, itemPath, item, itemSchema)
    })
    return
  }

  if (!isRecord(data)) {
    push(errors, path || '(root)', 'expected object')
    return
  }

  for (const [rawKey, childSchema] of Object.entries(schema)) {
    const { key, optional } = splitSchemaKey(rawKey)
    const childPath = path ? `${path}.${key}` : key
    if (!(key in data) || data[key] === undefined) {
      if (!optional) {
        push(errors, childPath, `missing required field "${key}"`)
      }
      continue
    }
    if (data[key] === null) {
      push(errors, childPath, 'null is not allowed; omit optional fields instead')
      continue
    }
    walk(errors, childPath, data[key], childSchema)
  }
}
